import {Component, EventEmitter, HostListener, Input, Output} from '@angular/core';
import {GlobalAlertService} from '../../_services/global-alert.service';
import {AlertLevel} from '../../enums/alert-level';
import {LegacyClaimStatus} from './migration.models';
import {AccountLinkOptionsComponent} from '../linking/account-link-options.component';
import {LEGACY_LOGIN_DISABLED_ON_TEXT} from './legacy-login-deadline';

/**
 * The migration notice (#279): explains the move to accounts to somebody who signed in with legacy credentials,
 * and offers the four ways to claim this login — Microsoft, Apple, Google, or an e-mail address and a password.
 *
 * The fourth is the one this screen was missing for longest, and its absence was not cosmetic. The three
 * provider buttons all require an account somewhere else; somebody with none of the three could read the notice,
 * agree with it, and have nothing to press. The invitation landing had already grown an e-mail + password form
 * for exactly that reason — but reaching it meant being invited, which the people this notice is shown to are
 * not: they already have access.
 *
 * Dismissible on purpose. It appears once on every legacy sign-in (once per session since #854, however it is
 * closed), which makes it the most-seen screen in the product during the migration window — a modal that could not be dismissed would block people from working over a
 * change they may not be ready to make. Which is precisely why the deadline sentence belongs HERE and nowhere
 * else on the screen: „Vorerst bei Benutzername und Passwort bleiben" is an offer somebody can take on every
 * sign-in until #264 lands, and „vorerst" is only an honest word once the reader knows when it runs out.
 *
 * The deadline is **not** on {@link AccountLinkOptionsComponent}, although both profile screens render that
 * component too. The doors are also the way an ALREADY-migrated session attaches a second authenticator, where a
 * sentence about losing access is simply untrue — the notice is the only one of the three surfaces that is shown
 * exclusively to a legacy session, so it is the only one that can state a legacy consequence.
 *
 * Transitional — deleted together with the legacy authorization system at #264, which is the event the deadline
 * announces.
 */
@Component({
  selector: 'auth-migration-notice',
  standalone: true,
  imports: [AccountLinkOptionsComponent],
  template: `
    <!-- #854: the X, a backdrop click and Escape all close the notice, like „Vorerst bei Benutzername und
         Passwort bleiben". Closing is always safe here: nothing is lost, and the four doors stay on the profile.
         Every way out emits the same dismissed event, so the host records "seen" for this session once, whichever
         one was used. None of them works mid-claim (busy), when a door is already navigating away. -->
    <div class="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4"
         data-testid="migration-notice-backdrop" (click)="close()">
      <div class="max-h-full w-full overflow-y-auto rounded-t-2xl bg-white p-5 shadow-xl sm:max-w-lg sm:rounded-2xl sm:p-6"
           role="dialog" aria-modal="true" aria-labelledby="migration-notice-title"
           (click)="$event.stopPropagation()">

        <div class="flex items-start justify-between gap-3">
          <h2 id="migration-notice-title" class="text-lg font-semibold text-gray-900">
            CheqMe stellt auf Konten um
          </h2>
          <button type="button" (click)="close()" [disabled]="busy" aria-label="Hinweis schließen"
                  class="-mr-1 -mt-1 rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-900 disabled:opacity-50">
            <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                 aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18"/>
            </svg>
          </button>
        </div>

        <p class="mt-2 text-sm text-gray-600">
          Künftig melden Sie sich mit Ihrem eigenen Konto an — mit Microsoft, Apple oder Google, oder mit einem
          Konto aus E-Mail-Adresse und Passwort. Ihr Zugang bleibt derselbe, nur der Weg hinein ändert sich.
        </p>

        <!-- The deadline. It sits directly under the intro, ahead of the steps and the doors, because it is the
             sentence that decides whether anybody reads the rest: the intro says WHAT changes, this says by
             when and what happens otherwise, and the steps and buttons below are then the answer to it.

             Deliberately NOT an alert card. No amber surface, no icon, no role="alert" — this notice is shown
             on every single legacy sign-in for the whole migration window, and a warning box seen that often
             is a warning box people learn to look past. It earns its weight structurally instead: a brand
             accent rule down the left, text-gray-900 against the intro's text-gray-600, and the date itself
             the only emphasised words on the screen. Loud enough to be unskippable, quiet enough that the four
             doors below stay the obvious thing to do.

             The date is interpolated, never typed. legacy-login-deadline.ts is the single source; typing
             „1. Januar 2027" here is exactly how the profile screens and the documentation would drift apart
             from it later. (No backticks anywhere in this comment — one would end the template literal; see the
             style guide, "A backtick inside an HTML comment".) -->
        <p class="mt-3 border-l-2 border-theme-primary-500 pl-3 text-sm text-gray-900">
          Ab dem <strong class="font-semibold">{{ disabledOn }}</strong> schaltet CheqMe die Anmeldung mit
          Benutzername und Passwort ab. Ohne ein verknüpftes Konto verlieren Sie dann Ihren Zugang.
        </p>

        <!-- The steps, in the order they actually happen. Kept concrete: "what will happen to me" is the
             question somebody has when a login screen changes under them.

             Step 1 named Microsoft until #279's native claim landed, and it was already wrong before that —
             wrong for the Apple and Google buttons the comment further down defends on exactly these grounds
             ("offering only Microsoft would quietly make the decision for them"). The buttons learned that
             lesson; the prose above them did not. It is read BEFORE the choice is made, so it must describe
             what happens under all four doors and name none of them. -->
        <ol class="mt-4 space-y-2 text-sm text-gray-600">
          <li class="flex gap-2">
            <span class="font-semibold text-theme-primary-700">1.</span>
            <span>Sie richten einmalig ein Konto ein — oder verwenden eines, das Sie schon haben.</span>
          </li>
          <li class="flex gap-2">
            <span class="font-semibold text-theme-primary-700">2.</span>
            <span>Ihre bisherigen Berechtigungen werden diesem Konto zugeordnet.</span>
          </li>
          <li class="flex gap-2">
            <span class="font-semibold text-theme-primary-700">3.</span>
            <span>Ab dann melden Sie sich mit diesem Konto an. Ihr Passwort funktioniert vorerst weiter.</span>
          </li>
        </ol>

        @if (status?.hasPendingInvitation) {
          <p class="mt-4 rounded-lg bg-theme-primary-50 p-3 text-sm text-theme-primary-900">
            Für Sie liegt bereits eine Einladung vor. Sie wird beim Verknüpfen automatisch eingelöst.
          </p>
        }

        <!-- Deliberately visible: this login can be claimed more than once, because several people may share
             it. Showing who already did is what makes that detectable. -->
        @if (status && status.claimedBy.length > 0) {
          <div class="mt-4 rounded-lg border-[1px] border-gray-100 bg-gray-50 p-3">
            <p class="text-xs font-semibold text-gray-700">Dieser Zugang ist bereits verknüpft mit:</p>
            <ul class="mt-1 space-y-0.5">
              @for (account of status.claimedBy; track account.identityId) {
                <li class="truncate text-xs text-gray-500">{{ account.displayName || account.email }}</li>
              }
            </ul>
          </div>
        }

        <p class="mt-5 text-sm font-medium text-gray-900">Womit möchten Sie sich künftig anmelden?</p>

        <!-- The four doors, shared with both profile screens (#279). They were three copies of the same
             handlers, which is how the employee profile came to offer one door and the wrong flow while this
             popup told people to go there. The claim idPrefix keeps the field ids this screen has always had
             (a backtick here would end the template literal — style guide, "A backtick inside an HTML comment"). -->
        <auth-account-link-options class="mt-2 block" idPrefix="claim" layout="stacked"
                                   [asEmployee]="asEmployee"
                                   (busyChange)="busy = $event"
                                   (claimed)="claimed.emit()"
                                   (failed)="reportStartFailure()"/>

        <button type="button" (click)="dismissed.emit()" [disabled]="busy"
                class="mt-3 w-full rounded-md border-[1px] border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50">
          Vorerst bei Benutzername und Passwort bleiben
        </button>

        <!-- text-gray-500, not text-gray-400. The footer is the promise that somebody who presses "stay on a
             password" can still find this later, so it has to be readable — and #9ca3af on white measures
             2.54:1, a serious axe finding at any text size. #6b7280 is 4.83:1, which clears AA for this 12px
             text. Found by the axe run that came with the deadline (migration-deadline-279.spec.ts): the
             alternative was exempting a real violation to make a new gate pass, which is how a baseline starts.
             See the style guide's contrast note — measure the composited pair, never trust the token name. -->
        <p class="mt-3 text-center text-xs text-gray-500">
          Sie finden das jederzeit im Profil unter &bdquo;Anmeldung &amp; Authentifizierung&ldquo;.
        </p>
      </div>
    </div>
  `
})
export class MigrationNoticeComponent {
  /**
   * The day the legacy login stops working, already in German („1. Januar 2027").
   *
   * Read from {@link LEGACY_LOGIN_DISABLED_ON_TEXT} rather than written into the template, so a slipped
   * deadline is one literal in one file and not a search across every screen that mentions it.
   */
  protected readonly disabledOn = LEGACY_LOGIN_DISABLED_ON_TEXT;

  /** The current session's migration state; null while it is still loading. */
  @Input() status: LegacyClaimStatus | null = null;

  /**
   * Whether this session is an employee rather than a management user.
   *
   * Only the three PROVIDER claims need it: their callback arrives as a top-level navigation with no bearer, so
   * the flow has to be told at the start. The native claim below passes nothing — the server classifies the
   * token it is already holding, which is both simpler and not forgeable.
   */
  @Input() asEmployee = false;

  /** "Remind me later". The host decides how long later is. */
  @Output() dismissed = new EventEmitter<void>();

  /**
   * Emitted when the native claim SUCCEEDED, so the host can show the same confirmation the provider claims
   * reach by coming back with `?claimed=success`.
   *
   * The provider flows have to round-trip through the browser and announce themselves in the URL. This one
   * never leaves the page, so it says so directly — which also means there is no query parameter to strip
   * afterwards and no reload for the person to sit through.
   */
  @Output() claimed = new EventEmitter<void>();

  /**
   * True while a door is being walked, mirrored up from the shared options component.
   *
   * Kept here for one reason: „Vorerst bei Benutzername und Passwort bleiben" must not be pressable mid-claim,
   * and that button belongs to this screen rather than to the options.
   */
  protected busy = false;

  constructor(private gas: GlobalAlertService) {
  }

  /**
   * The X, a backdrop click and Escape (#854). They mean the same as „Vorerst bei Benutzername und Passwort
   * bleiben": this person has seen the notice and chose not to act now. Ignored mid-claim, when a door is
   * already on its way to the provider.
   */
  protected close(): void {
    if (this.busy) return;
    this.dismissed.emit();
  }

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    this.close();
  }

  /**
   * A provider door would not start.
   *
   * Reported through the global alert rather than inline, because there is nothing on this screen for the
   * reader to correct — it is the redirect that failed, not anything they typed. The native door's refusals
   * are the opposite kind and are rendered beside the field they are about, inside the options component.
   */
  protected reportStartFailure(): void {
    this.gas.createAndShow('Fehler', 'Die Verknüpfung konnte nicht gestartet werden.', AlertLevel.error);
  }
}
